import React, { useMemo, useState } from 'react';
import countries from 'i18n-iso-countries';
// Import the languages you want to use
import enLocale from 'i18n-iso-countries/langs/en.json';
import { DropdownOption } from '@talentmesh/utils';
import { useFormContext } from 'react-hook-form';
import { RHFInput } from '@/Components/shadcn/rhf-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/shadcn/select';
import { cn } from '@/lib/utils';

interface CountrySelectorProps {
    name: string;
    label: string;
}

// Have to register the languages you want to use
countries.registerLocale(enLocale);

// Returns an object not a list
const countryObj = countries.getNames('en', { select: 'official' });

const countryOptions: DropdownOption[] = Object.entries(countryObj)
    .map(([key, value]) => {
        return {
            id: key,
            name: value,
        };
    })
    .sort((countryA, countryB) => countryA.name.localeCompare(countryB.name));

const CountrySelector = ({ label, name }: CountrySelectorProps) => {
    const { watch, setValue, getFieldState, formState } = useFormContext();
    const [isFocused, setIsFocused] = useState(false);
    const hasError = Boolean(getFieldState(name, formState).error);
    const selectedCountryCode = watch(name) as string | undefined;
    const selectedCountryName = useMemo(() => {
        if (!selectedCountryCode) {
            return '';
        }
        return countryOptions.find((country) => country.id === selectedCountryCode)?.name ?? '';
    }, [selectedCountryCode]);

    return (
        <RHFInput
            fullWidth
            label={label}
            name={name}
            displayValue={selectedCountryName}
            placeholder="Select country"
            readOnly
            tabIndex={-1}
            className={cn(
                'cursor-pointer',
                isFocused && !hasError && 'border-primary ring-primary ring-1 ring-offset-0',
                isFocused &&
                    hasError &&
                    'border-destructive ring-destructive dark:border-destructive/50 dark:ring-destructive/50 ring-1 ring-offset-0',
            )}
            inputOverlay={
                <Select
                    value={selectedCountryCode ?? ''}
                    onValueChange={(value) => {
                        setValue(name, value, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                        });
                    }}
                >
                    <SelectTrigger
                        aria-label={label}
                        className="absolute inset-0 h-full cursor-pointer border-0 opacity-0 shadow-none focus:ring-0 focus:ring-offset-0 focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    >
                        <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-72 overflow-y-auto">
                        {countryOptions.map((country) => (
                            <SelectItem key={country.id} value={country.id}>
                                {country.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            }
        />
    );
};
export default CountrySelector;
